<template>
  <div
    class="frequency-modal-wrapper"
    :class="{ 'frequency-modal-wrapper--active': saveScheduleResponse }"
  >
    <mds-loader
      class="loader"
      v-show="modalLoading && storedHolidays.length > 0"
    />
    <div
      v-show="!modalLoading && !saveScheduleResponse"
      class="frequency-modal-content"
    >
      <frequency-modal-header />
      <frequency-modal-content
        :without-job-name="withoutJobName"
        :frequency-form="frequencyForm"
        @input="addInfoToForm"
        @error="timeError = $event"
        @validDate="validDate = !$event"
        @validName="validName = $event"
        @name="jobName = $event"
        @selectedHolidays="selectedHolidays = $event"
      />
      <complementary-fields-wrapper
        :display-summary-time="disableSaveButton"
        :display-trigger-file="displayTriggerFile"
        :frequency-form="frequencyForm"
        :selected-holidays="selectedHolidays"
        @triggerFile="addInfoToForm"
      />
      <frequency-modal-buttons
        :disable-save-button="disableSaveButton"
        @save-schedule="saveSchedule"
        @close="$emit('close')"
      />
    </div>
    <scheduler-modal-save-response
      v-show="saveScheduleResponse"
      type="schedule"
      :has-duplicate-name="hasDuplicateName"
      :save-status="saveScheduleStatus"
      @save="saveSchedule"
      @close="closeModal"
      @change-name="renameSchedule"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import isEmpty from 'lodash/isEmpty'
import { zonedTimeToUtc } from 'date-fns-tz'
import MdsLoader from '@mds/loader'
import FrequencyModalHeader from '@/components/frequencyModal/FrequencyModalHeader.vue'
import FrequencyModalContent from '@/components/frequencyModal/FrequencyModalContent.vue'
import ComplementaryFieldsWrapper from '@/components/frequencyModal/frequencies/helpers/ComplementaryFieldsWrapper.vue'
import FrequencyModalButtons from '@/components/frequencyModal/FrequencyModalButtons.vue'
import SchedulerModalSaveResponse from '@/components/schedulerModal/SchedulerModalSaveResponse.vue'
import { formatSchedule, validateSchedule } from '@/utils/scheduleFormatter'
import {
  compareSchedules,
  formatEditSchedule,
  SCHEDULE_PROPS,
} from '@/utils/compareObjects'
import { formatDate, USER_PREFERENCES_TIMEZONE } from '@/utils/date'

export default {
  name: 'FrequencyModalWrapper',
  components: {
    MdsLoader,
    ComplementaryFieldsWrapper,
    FrequencyModalHeader,
    FrequencyModalContent,
    FrequencyModalButtons,
    SchedulerModalSaveResponse,
  },
  data() {
    return {
      jobName: '',
      timeError: false,
      withoutJobName: false,
      validName: true,
      validDate: true,
      frequencyForm: {},
      displayTriggerFile: false,
      selectedHolidays: [],
    }
  },
  props: {
    modalLoading: Boolean,
    saveScheduleResponse: Boolean,
    saveScheduleStatus: Boolean,
    hasDuplicateName: Boolean,
    feedId: undefined,
  },
  watch: {
    jobName() {
      this.validateJobName()
    },
    editedFeed: {
      deep: true,
      immediate: true,
      handler(feedParameters) {
        if (!isEmpty(feedParameters)) {
          this.triggerFileValidation(feedParameters)
        }
      },
    },
  },
  computed: {
    ...mapState({
      editedSchedule: (state) => state.cache.cache.editedSchedule,
      editedFeed: (state) => state.cache.cache.editedFeed,
      storedHolidays: (state) => state.cache.cache.holidays,
    }),
    validFrequencyForm() {
      return !!((!this.validName && this.withoutJobName) || !this.timeError)
    },
    disableSaveButton() {
      return (
        this.validFrequencyForm ||
        this.validDate ||
        !this.isScheduleUpdated ||
        this.frequencyForm?.dateType === 'undefined-Business'
      )
    },
    isScheduleUpdated() {
      if (!this.editedSchedule) {
        return true
      }
      const scheduleForm = validateSchedule(this.frequencyForm)
      const editedSchedule = formatEditSchedule(this.editedSchedule)
      const { time, date } = formatDate(
        zonedTimeToUtc(
          `${this.editedSchedule?.start_day} ${this.editedSchedule?.running_time}`,
          this.editedSchedule?.timezone
        )
      )
      return compareSchedules(
        { ...scheduleForm, monthlyOpt: '' },
        {
          ...editedSchedule,
          startDay: date,
          runningTime: time,
          timezone: USER_PREFERENCES_TIMEZONE,
        },
        SCHEDULE_PROPS
      )
    },
  },
  methods: {
    addInfoToForm(info) {
      const [key] = Object.keys(info)
      const [value] = Object.values(info)

      this.frequencyForm = {
        ...this.frequencyForm,
        [key]: value || false,
      }

      if (key === 'dateType') {
        this.$emit(
          'holidayRecurrence',
          (this.frequencyForm.repeatType === 'Daily' ||
            this.frequencyForm.repeatType === 'Monthly') &&
            value.includes('-Business')
        )
      }
    },
    saveSchedule() {
      if (!this.jobName) {
        this.withoutJobName = true
      } else {
        this.withoutJobName = false

        const schedule = formatSchedule(this.frequencyForm)
        this.$emit('createSchedule', schedule)
      }
    },
    validateJobName() {
      this.withoutJobName = !!(this.jobName || this.jobName === '')
    },
    closeModal() {
      this.$emit('close')
    },
    triggerFileValidation(feedParameters) {
      const { delivery_type = 'Email' } = feedParameters

      this.displayTriggerFile = delivery_type !== 'Email'
    },
    renameSchedule() {
      this.$emit('hide-feedback-message')
    },
  },
}
</script>

<style lang="scss" scoped>
.frequency-modal-wrapper,
.frequency-modal-content {
  display: flex;
  flex-direction: column;
  flex: auto;
  position: relative;

  &--active {
    justify-content: center;
    align-items: center;
  }
}

.loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
